import { Component, inject, Injector, Input, OnInit } from '@angular/core';
import { matches } from 'lodash';
import { AbstractWorkspaceComponent } from '../../shared/abstract-workspace-component';
import { Submission } from '../../team-apis/otterdam/submission';
import { Task } from '../../team-apis/otterdam/task';
import { ClientFeedbackLoadingService } from '../http/loading/client-feedback-loading.service';
import { workspaceProviders } from '../workspace-providers';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection -- tests fail with OnPush
@Component({
  selector: 'h3-read-only-workspace',
  templateUrl: './read-only-workspace.component.html',
  styleUrls: ['../../shared/styles/workspace-styles.scss'],
  providers: workspaceProviders,
  standalone: false,
})
export class ReadOnlyWorkspaceComponent
  extends AbstractWorkspaceComponent
  implements OnInit
{
  @Input({ required: true }) projectId!: number;
  @Input({ required: true }) stepId!: number;
  @Input({ required: true }) initialTaskId!: string;
  // submissions should be undefined for templates
  @Input() submissions?: Submission[];
  @Input() templateId?: string;

  private clientFeedbackLoadingService = inject(ClientFeedbackLoadingService);

  constructor(injector: Injector) {
    super('readOnly', injector);
    this.store('readOnly').set(true);
  }

  protected buildWorkspaceConfig() {
    return { user: this.currentUser, ...this };
  }

  protected override async loadModels() {
    this.modelsLoading$.next('loading');

    const submissionId = this.getSubmissionId();
    if (submissionId) {
      this.store('selectedSubmissionId').set(submissionId);
    }

    await this.projectLoadingService.load();
    const [task] = await Promise.all([
      this.taskLoadingService.loadForReadOnly(),
      this.featureFlagLoadingService.load(),
      this.taxonomyService.load(),
    ]);
    this.settingsLoadingService.load();

    this.store.setTask(task);

    if (!this.templateId) {
      await this.loadTaskModels(task);
    }
  }

  private async loadTaskModels(task: Task) {
    const submissionId = this.store.state().selectedSubmissionId;
    let submission: Submission | undefined;
    if (this.submissions && submissionId) {
      submission = this.submissions.find(matches({ id: submissionId }));
    }

    const commonModels = [
      this.annotationLoadingService.load(task, submission),
      this.loadPreAnnotationsIfAllowed(),
    ];

    if (submission) {
      await Promise.all([
        ...commonModels,
        this.feedbackLoadingService.load(submission),
      ]);
      this.store('feedbackType').set('shape');
    } else {
      await Promise.all([
        ...commonModels,
        this.clientFeedbackLoadingService.load(task),
        this.authorizationService.load(),
      ]);
      this.store('feedbackType').set('client');
    }
  }

  private getSubmissionId() {
    const params = this.browserControlService.getQueryParameters();
    const id = new URLSearchParams(params).get('submission_id');
    return id ? +id : undefined;
  }
}
